import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
	Check,
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	ChevronsRight,
	Copy,
	Download,
	ExternalLink,
	Flag,
	Info,
	Pencil,
	Plus,
	RefreshCw,
	Search,
	Star,
	Trash2,
	X
} from 'lucide-react';
import type { ReviewToken } from '@sleed/bizcommerce-api';
import { ReviewStatus } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Pill, type PillVariant } from '~/components/pill';
import { DateInput } from '~/components/date-input';
import { MultiSelect } from '~/components/multi-select';
import { ProductThumb } from '~/components/catalog/bundles/product-combobox';
import { formatDateTime } from '~/lib/format-date';
import { serverMessage } from '~/lib/api/server-error';
import { exportToCsv } from '~/lib/export-csv';
import { STOREFRONT_URL } from '~/lib/brand';
import { cycleSort, type GridSort } from '~/lib/grid/sort';
import {
	REVIEW_TARGET_TYPES,
	SORTABLE_REVIEW_COLUMNS,
	isProductTarget,
	reviewAuthorKind,
	reviewExportColumns,
	reviewTargetTypeKey,
	reviewTargetTypeLabel,
	reviewTargetName,
	reviewTargetVariant,
	reviewerName,
	storefrontUrl,
	togglePageSelection,
	type ReviewSortKey
} from '~/lib/reviews/reviews';
import {
	fetchReviewsForExport,
	useReviewBulkActions,
	useReviewsList,
	useReviewMutations,
	useReviewStatuses,
	type ReviewKind
} from '~/hooks/use-reviews';
import { SortableHeader } from '~/components/grid/sortable-header';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';
import { ReviewEditDialog } from './review-edit-dialog';
import { ReviewDetailsSheet } from './review-details-sheet';
import { ReviewCopyDialog } from './review-copy-dialog';
import { ReviewPhotoStrip } from './review-photos';

const PAGE_SIZE = 25;

/** Read-only 0–5 star rating; nothing for an unrated (editorial) review. */
function Stars({ value }: { value: number | null | undefined }) {
	if (value == null) return <span className="text-foreground-disabled">—</span>;
	const v = Math.round(value);
	return (
		<span className="inline-flex items-center gap-0.5" title={String(value)}>
			{Array.from({ length: 5 }, (_, i) => (
				<Star key={i} size={13} className={i < v ? 'text-amber-500' : 'text-foreground-disabled'} fill={i < v ? 'currentColor' : 'none'} />
			))}
		</span>
	);
}

function statusVariant(st: number | undefined): PillVariant {
	switch (st) {
		case ReviewStatus.Accepted:
			return 'success';
		case ReviewStatus.Declined:
			return 'danger';
		case ReviewStatus.Revisioned:
			return 'info';
		default:
			return 'warning';
	}
}

const RATINGS = [1, 2, 3, 4, 5];
const ANY = '__any__';
const COLUMNS = 9;

type BulkAction = 'approve' | 'decline' | 'delete';

/** Server-paginated reviews grid (customer comments or contributor reviews) with filter bar, sort, export + moderation. */
export function ReviewsGrid({ kind }: { kind: ReviewKind }) {
	const { t } = useTranslation();
	const { data: statuses } = useReviewStatuses();
	const editorial = kind === 'contributor';
	const sortableColumns = SORTABLE_REVIEW_COLUMNS[kind];

	const [page, setPage] = useState(0);
	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);
	const [ratingFrom, setRatingFrom] = useState<number | null>(null);
	const [ratingTo, setRatingTo] = useState<number | null>(null);
	const [dateFrom, setDateFrom] = useState<string | null>(null);
	const [dateTo, setDateTo] = useState<string | null>(null);
	const [sort, setSort] = useState<GridSort | null>(null);
	const [reportedOnly, setReportedOnly] = useState(false);
	const [targetTypes, setTargetTypes] = useState<number[]>([]);
	const [exporting, setExporting] = useState(false);

	// Debounce the free-text search so we don't refetch on every keystroke.
	useEffect(() => {
		const id = setTimeout(() => {
			setSearch(searchInput);
			setPage(0);
		}, 350);
		return () => clearTimeout(id);
	}, [searchInput]);

	const filters = useMemo(
		() => ({ search, statuses: selectedStatuses, ratingFrom, ratingTo, dateFrom, dateTo, sort, reported: reportedOnly, targetTypes }),
		[search, selectedStatuses, ratingFrom, ratingTo, dateFrom, dateTo, sort, reportedOnly, targetTypes]
	);
	const targetTypeOptions = useMemo(
		() => REVIEW_TARGET_TYPES[kind].map(id => ({ id: id as number, label: t(reviewTargetTypeKey(id)) })),
		[kind, t]
	);
	const params = useMemo(() => ({ skip: page * PAGE_SIZE, take: PAGE_SIZE, ...filters }), [page, filters]);

	const { data, isLoading, isError, isFetching, refetch } = useReviewsList(kind, params);
	const { update, remove } = useReviewMutations(kind);
	const bulk = useReviewBulkActions();

	// Bulk selection: the rows on screen only — it is cleared whenever the page, filters or sort change, so an action
	// never reaches a row the admin can no longer see.
	const [selected, setSelected] = useState<Set<number>>(new Set());
	const [bulkAction, setBulkAction] = useState<BulkAction | null>(null);
	useEffect(() => setSelected(new Set()), [params]);

	// `undefined` = closed; `null` = adding a new editorial review.
	const [editRow, setEditRow] = useState<ReviewToken | null | undefined>(undefined);
	const [deleteRow, setDeleteRow] = useState<ReviewToken | null>(null);
	const [detailsRow, setDetailsRow] = useState<ReviewToken | null>(null);
	const [copyRow, setCopyRow] = useState<ReviewToken | null>(null);

	const rows = data?.items ?? [];
	const total = data?.totalCount ?? 0;
	const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
	const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
	const to = Math.min((page + 1) * PAGE_SIZE, total);
	const pageIds = rows.map(r => r.id).filter((id): id is number => id != null);
	const allOnPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));

	const toggleOne = (id: number) =>
		setSelected(prev => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});

	const runBulk = () => {
		const ids = [...selected];
		if (!bulkAction || ids.length === 0) return;
		const done = {
			onSuccess: (count: number) => {
				toast.success(t(`catalog.reviews.bulk.done.${bulkAction}`, { count }));
				setSelected(new Set());
				setBulkAction(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		};
		if (bulkAction === 'delete') bulk.remove.mutate(ids, done);
		else bulk.setStatus.mutate({ ids, st: bulkAction === 'approve' ? ReviewStatus.Accepted : ReviewStatus.Declined }, done);
	};
	const bulkPending = bulk.setStatus.isPending || bulk.remove.isPending;

	const onSort = (column: ReviewSortKey) => {
		setSort(prev => cycleSort(prev, column));
		setPage(0);
	};

	const setStatus = (r: ReviewToken, st: ReviewStatus) => {
		if (!r.id) return;
		update.mutate(
			{ ...r, st },
			{
				onSuccess: () => toast.success(t('common.save')),
				onError: err => toast.error(serverMessage(err))
			}
		);
	};

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	// The filtered set in the current order (not the page on screen), as the legacy grid's export gave; a cut export says so.
	const exportCsv = async () => {
		setExporting(true);
		try {
			const { items, totalCount } = await fetchReviewsForExport(kind, filters);
			exportToCsv(
				editorial ? 'contributor-reviews' : 'customer-reviews',
				reviewExportColumns(kind, key => t(key)),
				items
			);
			if (totalCount > items.length) toast.warning(t('catalog.reviews.export.truncated', { exported: items.length, total: totalCount }));
		} catch (err) {
			toast.error(serverMessage(err));
		} finally {
			setExporting(false);
		}
	};

	const resetFilters = () => {
		setSearchInput('');
		setSelectedStatuses([]);
		setRatingFrom(null);
		setRatingTo(null);
		setDateFrom(null);
		setDateTo(null);
		setSort(null);
		setReportedOnly(false);
		setTargetTypes([]);
		setPage(0);
	};

	// The shared grid header (Greek capitals, sort affordance), as in the orders and returns grids.
	const th = (column: ReviewSortKey, label: string, align: 'left' | 'center' = 'left') => {
		const active = sort?.key === column ? sort.dir : null;
		return (
			<th
				className={cn('px-3 py-2 whitespace-nowrap', align === 'center' && 'text-center')}
				aria-sort={active ? (active === 'asc' ? 'ascending' : 'descending') : undefined}>
				<SortableHeader
					label={label}
					align={align}
					active={active}
					onSort={sortableColumns.includes(column) ? () => onSort(column) : undefined}
				/>
			</th>
		);
	};

	return (
		<div className="flex flex-col gap-3">
			{/* Filter bar */}
			<div className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-surface p-3">
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-foreground-subtle">{t('common.search')}</label>
					<div className="relative w-60">
						<Search size={16} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-foreground-subtle" />
						<Input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder={t('common.search')} className="pl-8" />
					</div>
				</div>
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-foreground-subtle">{t('catalog.reviews.columns.status')}</label>
					<div className="w-56">
						<MultiSelect
							value={(statuses ?? []).filter(s => s.id != null && selectedStatuses.includes(s.id))}
							onChange={next => {
								setSelectedStatuses(next.map(s => s.id!).filter(v => v != null));
								setPage(0);
							}}
							options={statuses ?? []}
							getKey={s => String(s.id)}
							getLabel={s => s.nm ?? ''}
							placeholder={t('catalog.reviews.allStatuses')}
						/>
					</div>
				</div>
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-foreground-subtle">{t('catalog.reviews.targetType')}</label>
					<div className="w-56">
						<MultiSelect
							value={targetTypeOptions.filter(o => targetTypes.includes(o.id))}
							onChange={next => {
								setTargetTypes(next.map(o => o.id));
								setPage(0);
							}}
							options={targetTypeOptions}
							getKey={o => String(o.id)}
							getLabel={o => o.label}
							placeholder={t('catalog.reviews.allTargetTypes')}
						/>
					</div>
				</div>
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-foreground-subtle">{t('catalog.reviews.ratingFrom')}</label>
					<Select
						value={ratingFrom != null ? String(ratingFrom) : ANY}
						onValueChange={v => {
							setRatingFrom(v === ANY ? null : Number(v));
							setPage(0);
						}}>
						<SelectTrigger className="w-28">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ANY}>{t('catalog.reviews.any')}</SelectItem>
							{RATINGS.map(r => (
								<SelectItem key={r} value={String(r)}>
									{r}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-foreground-subtle">{t('catalog.reviews.ratingTo')}</label>
					<Select
						value={ratingTo != null ? String(ratingTo) : ANY}
						onValueChange={v => {
							setRatingTo(v === ANY ? null : Number(v));
							setPage(0);
						}}>
						<SelectTrigger className="w-28">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ANY}>{t('catalog.reviews.any')}</SelectItem>
							{RATINGS.map(r => (
								<SelectItem key={r} value={String(r)}>
									{r}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-foreground-subtle">{t('catalog.reviews.dateFrom')}</label>
					<DateInput
						value={dateFrom ? new Date(dateFrom) : null}
						onChange={d => {
							setDateFrom(d ? d.toISOString() : null);
							setPage(0);
						}}
						placeholder="—"
					/>
				</div>
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-foreground-subtle">{t('catalog.reviews.dateTo')}</label>
					<DateInput
						value={dateTo ? new Date(dateTo) : null}
						onChange={d => {
							setDateTo(d ? d.toISOString() : null);
							setPage(0);
						}}
						placeholder="—"
					/>
				</div>
				<div className="flex h-9 items-center gap-2">
					<Switch
						id={`rv-reported-${kind}`}
						checked={reportedOnly}
						onCheckedChange={v => {
							setReportedOnly(v);
							setPage(0);
						}}
					/>
					<Label htmlFor={`rv-reported-${kind}`} className="text-xs font-medium text-foreground-subtle">
						{t('catalog.reviews.reports.onlyReported')}
					</Label>
				</div>
				<Button variant="ghost" onClick={resetFilters}>
					{t('catalog.reviews.clearFilters')}
				</Button>
				<div className="ml-auto flex items-end gap-2">
					<Button variant="outline" onClick={() => void exportCsv()} disabled={exporting || total === 0}>
						<Download size={15} /> {t('catalog.reviews.export.button')}
					</Button>
					{editorial && (
						<Button onClick={() => setEditRow(null)}>
							<Plus size={15} /> {t('catalog.reviews.addButton')}
						</Button>
					)}
				</div>
			</div>

			{selected.size > 0 && (
				<div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-elevated px-3 py-2 text-sm">
					<span className="text-foreground-muted">{t('catalog.reviews.bulk.selected', { count: selected.size })}</span>
					<div className="flex flex-wrap items-center gap-2">
						<Button variant="outline" size="sm" onClick={() => setBulkAction('approve')} disabled={bulkPending}>
							<Check size={14} className="text-success-fg" /> {t('catalog.reviews.approve')}
						</Button>
						<Button variant="outline" size="sm" onClick={() => setBulkAction('decline')} disabled={bulkPending}>
							<X size={14} className="text-danger-fg" /> {t('catalog.reviews.decline')}
						</Button>
						<Button variant="outline" size="sm" onClick={() => setBulkAction('delete')} disabled={bulkPending}>
							<Trash2 size={14} className="text-danger-fg" /> {t('common.delete')}
						</Button>
						<Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
							{t('catalog.reviews.bulk.clear')}
						</Button>
					</div>
				</div>
			)}

			{/* Grid bar */}
			<div className="flex items-center justify-between gap-4 rounded-t-md border border-b-0 border-border bg-elevated px-3 py-2 text-xs text-foreground-muted">
				<span>
					{t('system.grid.showing')}{' '}
					<strong className="font-medium tabular-nums text-foreground">
						{from}–{to}
					</strong>{' '}
					{t('system.grid.of')} <strong className="font-medium tabular-nums text-foreground">{total.toLocaleString('el-GR')}</strong>
				</span>
				<button
					type="button"
					onClick={() => void refetch()}
					title={t('common.refresh')}
					className="rounded-sm p-1.5 text-foreground-muted hover:bg-muted hover:text-foreground">
					<RefreshCw size={13} className={isFetching ? 'animate-spin' : undefined} />
				</button>
			</div>

			{/* Table */}
			<div className="overflow-x-auto rounded-b-md border border-border bg-surface">
				<table className="w-full border-collapse text-left text-sm" style={{ minWidth: 1120 }}>
					<thead>
						<tr className="border-b border-border bg-elevated text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">
							<th className="w-[40px] px-3 py-2">
								<input
									type="checkbox"
									className="h-4 w-4 cursor-pointer"
									checked={allOnPageSelected}
									onChange={() => setSelected(prev => togglePageSelection(prev, pageIds))}
									aria-label={t('catalog.reviews.bulk.selectAll')}
								/>
							</th>
							<th className="px-3 py-2">{toGreekCaps(t('catalog.reviews.columns.target'))}</th>
							{th('reviewer', t('catalog.reviews.columns.reviewer'))}
							{th('rating', t('catalog.reviews.columns.rating'), 'center')}
							{th('status', t('catalog.reviews.columns.status'), 'center')}
							{th('date', t('catalog.reviews.columns.date'))}
							{th('helpful', t('catalog.reviews.columns.helpful'), 'center')}
							<th className="px-3 py-2">{toGreekCaps(t('catalog.reviews.columns.comment'))}</th>
							<th className="px-3 py-2" />
						</tr>
					</thead>
					<tbody>
						{isError ? (
							<tr>
								<td colSpan={COLUMNS} className="px-3 py-12 text-center text-foreground-muted">
									{t('common.noResults')}
								</td>
							</tr>
						) : isLoading ? (
							<tr>
								<td colSpan={COLUMNS} className="p-4">
									<Skeleton className="h-64 w-full rounded-md" />
								</td>
							</tr>
						) : rows.length === 0 ? (
							<tr>
								<td colSpan={COLUMNS} className="px-3 py-12 text-center text-foreground-muted">
									{t('common.noResults')}
								</td>
							</tr>
						) : (
							rows.map(r => {
								const targetUrl = storefrontUrl(STOREFRONT_URL, r.eurl);
								const author = reviewAuthorKind(kind, r);
								return (
									<tr key={r.id} className="group border-b border-border-subtle last:border-b-0 hover:bg-elevated">
										<td className="px-3 py-2.5">
											<input
												type="checkbox"
												className="h-4 w-4 cursor-pointer"
												checked={r.id != null && selected.has(r.id)}
												onChange={() => r.id != null && toggleOne(r.id)}
												aria-label={t('catalog.reviews.bulk.selectRow')}
											/>
										</td>
										<td className="px-3 py-2.5">
											<div className="flex items-center gap-2">
												<ProductThumb src={r.imgUrl} alt={r.to_title} size={32} />
												<div className="min-w-0">
													<Pill variant={reviewTargetVariant(r.to_ent)}>{reviewTargetTypeLabel(t, r.to_ent, r.to_ent_nm)}</Pill>
													<div className="mt-0.5 flex items-center gap-1">
														<span className="truncate font-medium text-foreground" title={r.to_title ?? ''}>
															{reviewTargetName(r.to_title, r.to_id)}
														</span>
														{targetUrl && (
															<a
																href={targetUrl}
																target="_blank"
																rel="noreferrer"
																title={t('catalog.reviews.openTarget')}
																aria-label={t('catalog.reviews.openTarget')}
																className="shrink-0 text-foreground-subtle hover:text-foreground">
																<ExternalLink size={12} />
															</a>
														)}
													</div>
													{r.to_code && <div className="truncate font-mono text-[11px] text-foreground-muted">{r.to_code}</div>}
												</div>
											</div>
										</td>
										<td className="px-3 py-2.5">
											<div className="text-[10px] text-foreground-subtle">
												{t(author === 'customer' ? 'catalog.reviews.author.customer' : 'catalog.reviews.author.editorial')}
											</div>
											<div className="truncate text-foreground">{reviewerName(kind, r) || '—'}</div>
											{author === 'customer' && r.cust_eml && (
												<div className="truncate text-[11px] text-foreground-muted">{r.cust_eml}</div>
											)}
											{author === 'contributor' && (r.cat_name || r.source) && (
												<div className="truncate text-[11px] text-foreground-muted">
													{[r.cat_name, r.source].filter(Boolean).join(' · ')}
												</div>
											)}
										</td>
										<td className="px-3 py-2.5 text-center">
											<div className="flex justify-center">
												<Stars value={r.rating} />
											</div>
										</td>
										<td className="px-3 py-2.5 text-center">
											<div className="flex flex-col items-center gap-1">
												<Pill variant={statusVariant(r.st)}>{r.st_nm || '—'}</Pill>
												{r.isFeatured && <span className="text-[10px] text-foreground-muted">{t('catalog.reviews.featured')}</span>}
												{(r.open_reports ?? 0) > 0 && (
													<button
														type="button"
														onClick={() => setDetailsRow(r)}
														title={t('catalog.reviews.reports.openCount', { count: r.open_reports ?? 0 })}
														className="inline-flex items-center gap-1 rounded-pill bg-danger px-1.5 py-px text-[10px] font-semibold tabular-nums text-danger-fg">
														<Flag size={10} /> {r.open_reports}
													</button>
												)}
											</div>
										</td>
										<td className="px-3 py-2.5 whitespace-nowrap text-xs tabular-nums text-foreground-muted">
											{formatDateTime(r.review_date)}
										</td>
										<td className="px-3 py-2.5 text-center text-xs tabular-nums text-foreground-muted">{r.helpful_count ?? 0}</td>
										<td className="px-3 py-2.5">
											{r.tl && <div className="truncate font-medium text-foreground">{r.tl}</div>}
											<span className="line-clamp-2 text-foreground-muted" title={r.review ?? ''}>
												{r.review || '—'}
											</span>
											{!editorial && <ReviewPhotoStrip photos={r.photos} />}
										</td>
										<td className="px-3 py-2.5">
											<div className="flex items-center justify-end gap-0.5">
												{!editorial && (
													<>
														<Button
															variant="ghost"
															size="icon"
															aria-label={t('catalog.reviews.approve')}
															title={t('catalog.reviews.approve')}
															disabled={update.isPending}
															onClick={() => setStatus(r, ReviewStatus.Accepted)}>
															<Check size={15} className="text-success-fg" />
														</Button>
														<Button
															variant="ghost"
															size="icon"
															aria-label={t('catalog.reviews.decline')}
															title={t('catalog.reviews.decline')}
															disabled={update.isPending}
															onClick={() => setStatus(r, ReviewStatus.Declined)}>
															<X size={15} className="text-danger-fg" />
														</Button>
														<Button
															variant="ghost"
															size="icon"
															aria-label={t('catalog.reviews.details.title')}
															title={t('catalog.reviews.details.title')}
															onClick={() => setDetailsRow(r)}>
															<Info size={15} />
														</Button>
													</>
												)}
												{editorial && (
													<Button
														variant="ghost"
														size="icon"
														aria-label={t('catalog.reviews.details.title')}
														title={t('catalog.reviews.details.title')}
														onClick={() => setDetailsRow(r)}>
														<Info size={15} />
													</Button>
												)}
												{editorial && isProductTarget(r.to_ent) && (
													<Button
														variant="ghost"
														size="icon"
														aria-label={t('catalog.reviews.copy.title')}
														title={t('catalog.reviews.copy.title')}
														onClick={() => setCopyRow(r)}>
														<Copy size={15} />
													</Button>
												)}
												<Button variant="ghost" size="icon" aria-label={t('common.edit')} onClick={() => setEditRow(r)}>
													<Pencil size={15} />
												</Button>
												<Button variant="ghost" size="icon" aria-label={t('common.delete')} onClick={() => setDeleteRow(r)}>
													<Trash2 size={15} className="text-danger-fg" />
												</Button>
											</div>
										</td>
									</tr>
								);
							})
						)}
					</tbody>
				</table>
			</div>

			{/* Pager */}
			<div className="mt-1 flex items-center justify-end gap-1 rounded-md border border-border bg-surface px-3 py-2 text-xs text-foreground-muted">
				<Button
					variant="outline"
					size="icon"
					type="button"
					disabled={page === 0}
					onClick={() => setPage(0)}
					aria-label={t('system.grid.first')}>
					<ChevronsLeft size={15} />
				</Button>
				<Button
					variant="outline"
					size="icon"
					type="button"
					disabled={page === 0}
					onClick={() => setPage(page - 1)}
					aria-label={t('system.grid.prev')}>
					<ChevronLeft size={15} />
				</Button>
				<span className="px-2">
					{page + 1} / {pageCount}
				</span>
				<Button
					variant="outline"
					size="icon"
					type="button"
					disabled={page >= pageCount - 1}
					onClick={() => setPage(page + 1)}
					aria-label={t('system.grid.next')}>
					<ChevronRight size={15} />
				</Button>
				<Button
					variant="outline"
					size="icon"
					type="button"
					disabled={page >= pageCount - 1}
					onClick={() => setPage(pageCount - 1)}
					aria-label={t('system.grid.last')}>
					<ChevronsRight size={15} />
				</Button>
			</div>

			<ReviewEditDialog
				kind={kind}
				open={editRow !== undefined}
				row={editRow ?? null}
				onOpenChange={open => !open && setEditRow(undefined)}
			/>
			<ReviewDetailsSheet kind={kind} row={detailsRow} onOpenChange={open => !open && setDetailsRow(null)} />
			{editorial && <ReviewCopyDialog row={copyRow} onOpenChange={open => !open && setCopyRow(null)} />}

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('catalog.reviews.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('catalog.reviews.deleteBody')}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={bulkAction != null} onOpenChange={open => !open && setBulkAction(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{bulkAction ? t(`catalog.reviews.bulk.${bulkAction}Title`, { count: selected.size }) : ''}</DialogTitle>
						<DialogDescription>
							{bulkAction === 'approve' && !editorial
								? t('catalog.reviews.bulk.approveCustomerBody', { count: selected.size })
								: bulkAction
									? t(`catalog.reviews.bulk.${bulkAction}Body`, { count: selected.size })
									: ''}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setBulkAction(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant={bulkAction === 'delete' ? 'destructive' : 'default'} onClick={runBulk} disabled={bulkPending}>
							{bulkAction ? t(`catalog.reviews.bulk.${bulkAction}Confirm`, { count: selected.size }) : ''}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
